import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Mail, Calendar, CheckCircle2, LogOut, Bell, Sparkles, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

export default function MonCompte() {
  const { user, loading, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (loading) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center">
        <div className="text-neutral-400">Chargement...</div>
      </section>
    );
  }
  if (!user) {
    return <Navigate to="/connexion" replace state={{ from: '/mon-compte' }} />;
  }

  const handleLogout = () => {
    logout();
    toast({ title: 'Déconnecté', description: 'À bientôt dans la ruche.' });
    navigate('/');
  };

  const subscribedAt = user.subscribed_at ? new Date(user.subscribed_at).toLocaleString('fr-FR') : '—';
  const verifiedAt = user.verified_at ? new Date(user.verified_at).toLocaleString('fr-FR') : '—';

  return (
    <div>
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 grid place-items-center rounded-md bg-yellow-400/15 text-yellow-400">
            <ShieldCheck size={22} />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-white">Mon compte</h1>
            <p className="text-neutral-400 text-sm">Bienvenue{user.name ? `, ${user.name}` : ''} 🐝</p>
          </div>
        </div>

        <div className="grid gap-4">
          <div className="p-6 rounded-2xl border border-white/10 bg-neutral-900/40">
            <div className="flex items-start gap-3">
              <Mail size={18} className="text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-xs uppercase text-neutral-500 tracking-wider">Email</div>
                <div className="text-white break-all">{user.email}</div>
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-500/15 text-green-400 border border-green-500/30">
                <CheckCircle2 size={12} /> Vérifié
              </span>
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-white/10 bg-neutral-900/40 space-y-4">
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-xs uppercase text-neutral-500 tracking-wider">Inscrit le</div>
                <div className="text-white">{subscribedAt}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-xs uppercase text-neutral-500 tracking-wider">Vérifié le</div>
                <div className="text-white">{verifiedAt}</div>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-white/10 bg-neutral-900/40">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Bell size={16} className="text-yellow-400" /> Notifications</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between p-3 rounded-md bg-neutral-950/40 border border-white/5">
                <span className="text-neutral-300">Sortie officielle de l'app</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${user.notify_launch ? 'bg-yellow-400/15 text-yellow-300' : 'bg-neutral-800 text-neutral-500'}`}>
                  {user.notify_launch ? 'Activé' : 'Désactivé'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md bg-neutral-950/40 border border-white/5">
                <span className="text-neutral-300">Avancement du projet</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${user.notify_updates ? 'bg-yellow-400/15 text-yellow-300' : 'bg-neutral-800 text-neutral-500'}`}>
                  {user.notify_updates ? 'Activé' : 'Désactivé'}
                </span>
              </div>
            </div>
            <p className="mt-3 text-xs text-neutral-500">
              Pour modifier ces préférences, contactez <a href="mailto:contact.buzzbump@gmail.com" className="text-yellow-400 hover:underline">contact.buzzbump@gmail.com</a>.
            </p>
          </div>

          <button onClick={handleLogout}
            className="self-start inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/15 text-neutral-200 hover:border-red-400 hover:text-red-400 transition-colors">
            <LogOut size={16} /> Se déconnecter
          </button>
        </div>
      </section>
    </div>
  );
}
